import { Logger, LoggerService } from "@Shared/logger";
import { DataUtil, JwtUtil } from "@Shared/utils";
import { APP_EVENTS } from "@Core";
import { Bus } from "@Modules/event_module";
import { Auth0Service, AUTH0_CONST } from "@Modules/auth_module";
import { OneTapOptions } from "../_models";

const GIS_SCRIPT_URL = "https://accounts.google.com/gsi/client";

declare global {
    interface Window {
        google?: any;
    }
}

export class OneTapService {
    private Logger: Logger = LoggerService.createLogger("OneTapService");
    private static instance: OneTapService;

    static get Instance(): OneTapService {
        if (this.instance === null || this.instance === undefined) {
            this.instance = new OneTapService();
        }
        return this.instance;
    }

    private options: OneTapOptions;
    private gisLoadPromise: Promise<void> | null = null;

    constructor() {
        this.Logger.trace("****  CTOR ****");
    }

    public async init(options: OneTapOptions): Promise<void> {
        this.options = options;
        if (!options?.enabled) {
            this.Logger.trace("One Tap is not enabled, skipping init");
            return;
        }
        if (!options.clientId) {
            this.Logger.warn("One Tap enabled but no clientId provided, skipping");
            return;
        }

        const isAuthenticated = await Auth0Service.Instance.isAuthenticated().catch(() => false);
        if (isAuthenticated) {
            this.Logger.trace("User already authenticated, skipping One Tap");
            return;
        }

        const path = window.location.pathname;
        if (path.startsWith("/login") || path.startsWith("/logout") || path.startsWith("/callback")) {
            this.Logger.trace(`On ${path}, skipping One Tap`);
            return;
        }

        const loaded = await this.ensureGisLoaded();
        if (!loaded) return;

        this.promptOneTap();
    }

    public async promptManually(): Promise<void> {
        if (!this.options?.clientId) {
            this.Logger.warn("Cannot prompt One Tap manually — no clientId configured (call MNGAuth.init with oneTapOptions first)");
            return;
        }
        const loaded = await this.ensureGisLoaded();
        if (!loaded) return;
        this.Logger.trace("Manual One Tap prompt requested");
        this.promptOneTap();
    }

    private ensureGisLoaded(): Promise<boolean> {
        if (window.google?.accounts?.id) return Promise.resolve(true);
        if (!this.gisLoadPromise) {
            Bus.emit(APP_EVENTS.onScriptLoadStart, { scriptName: "GoogleIdentityServices" });
            this.gisLoadPromise = DataUtil.loadScript(GIS_SCRIPT_URL, true).then(() => undefined).finally(() => {
                Bus.emit(APP_EVENTS.onScriptLoadEnd, { scriptName: "GoogleIdentityServices" });
            });
        }
        return this.gisLoadPromise
            .then(() => !!window.google?.accounts?.id)
            .catch((error) => {
                this.Logger.error("Failed to load Google Identity Services script", error);
                this.gisLoadPromise = null;
                return false;
            });
    }

    private promptOneTap(): void {
        if (!window.google?.accounts?.id) {
            this.Logger.warn("google.accounts.id not available, cannot prompt");
            return;
        }
        this.Logger.trace("Initializing Google One Tap");
        window.google.accounts.id.initialize({
            client_id: this.options.clientId,
            callback: (token: any) => this.handleCredentialResponse(token),
            auto_select: false,
            cancel_on_tap_outside: false,
        });
        window.google.accounts.id.prompt((notification: any) => {
            let state: string | null = null;
            if (notification.isDisplayed && notification.isDisplayed()) {
                state = "displayed";
            } else if (notification.isSkippedMoment && notification.isSkippedMoment()) {
                state = "cancelled";
            } else if (
                notification.isNotDisplayed &&
                notification.isNotDisplayed() &&
                notification.getNotDisplayedReason &&
                notification.getNotDisplayedReason() === "suppressed_by_user"
            ) {
                state = "auto_hidden";
            }
            if (state) {
                Bus.emitAndTrack(APP_EVENTS.onOneTapPrompted, { state }, `one_tap_${state}`);
            }
        });
    }

    private async handleCredentialResponse(token: { credential: string }): Promise<void> {
        this.Logger.trace("One Tap credential received");
        Bus.emitAndTrack(APP_EVENTS.onOneTapLoginStarted, { loginsource: AUTH0_CONST.loginSourceGoogleOneTap }, "one_tap_click");
        try {
            const decoded = JwtUtil.decodeToken(token.credential);
            const email = decoded?.email;
            if (!email) {
                this.Logger.warn("One Tap credential missing email, aborting");
                return;
            }
            await Auth0Service.Instance.loginViaOneTap(email, this.options.useTribMainGoogle);
        } catch (error) {
            this.Logger.error("Error handling One Tap credential", error);
        }
    }
}
